import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {companyApi, promptApi} from '../../services/api';
import {BrandSafetyPrompt, Company, PaginationInfo} from '../../types';
import {sortByUpdatedDesc} from './sortPrompts';

const ITEMS_PER_PAGE = 10;

interface AuthUser {
    id: string;
    role: 'user' | 'eskimi_admin' | 'adops' | 'admin';
}

/**
 * Owns the Blueprints list: search/pagination state, the ?blueprintId=
 * isolation deep-link (bd-tkpeg) and the ?search= deep-link (bd-wxgk9),
 * loading/reloading, and the client-side updatedAt-DESC sort. Extracted from
 * PromptManager.tsx (deepcontext-ityf decomposition) — behavior unchanged.
 */
export function usePromptList(user: AuthUser | null | undefined, selectedCompanyId: string | null | undefined, setError: (msg: string | null) => void) {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialSearch = searchParams.get('search') ?? '';
    // bd-tkpeg — dedicated single-blueprint isolation (mirrors ScheduledJobs'
    // ?jobId= banner). Seeded from the URL on mount so direct paste / refresh
    // works.
    const [isolatedBlueprintId, setIsolatedBlueprintId] = useState<string>(
        () => searchParams.get('blueprintId') ?? '',
    );

    const [prompts, setPrompts] = useState<BrandSafetyPrompt[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const [searchTerm, setSearchTerm] = useState(initialSearch);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch);
    const [showOnlyMyBlueprints, setShowOnlyMyBlueprints] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const loadPrompts = useCallback(async (page: number = 1, search?: string, onlyMine: boolean = false) => {
        try {
            setIsLoading(true);
            // "Show only my blueprints" is a BACKEND filter (bd zh7jq): when on,
            // the server scopes strictly to the authenticated user's id and
            // ignores company scoping, so we drop the company filter entirely.
            const companyIdToUse = onlyMine
                ? undefined
                : (user?.role === 'eskimi_admin' || user?.role === 'adops') ? selectedCompanyId || undefined : undefined;

            const response = await promptApi.getAllPaginated({
                page,
                pageSize: ITEMS_PER_PAGE,
                search: search || undefined,
                companyId: companyIdToUse,
                onlyMine: onlyMine || undefined
            });

            setPrompts(response.prompts);
            setTotalCount(response.pagination.total);
            setTotalPages(response.pagination.totalPages);
            setCurrentPage(response.pagination.page);
        } catch (err) {
            setError('Failed to load prompts');
        } finally {
            setIsLoading(false);
        }
    }, [user?.role, selectedCompanyId, setError]);

    const loadCompanies = useCallback(async () => {
        try {
            const companiesData = await companyApi.getAll();
            setCompanies(companiesData);
        } catch (err) {
            console.error('Failed to load companies:', err);
            // Set an empty array to prevent repeated failed attempts
            setCompanies([]);
        }
    }, []);

    // Track if initial load has been done to prevent duplicate API calls (React StrictMode)
    // Using useRef because state gets reset between StrictMode double-mounts, but refs persist
    const lastLoadKey = React.useRef<string | null>(null);
    // Track the last search/page key to prevent duplicate calls from the search/pagination effect.
    // bd-wxgk9 — seed from the deep-link search so the initial filtered load isn't deduped away.
    const lastSearchPageKey = React.useRef<string>(`1-${initialSearch}`);

    useEffect(() => {
        // Skip if user is not yet loaded
        if (!user) {
            return;
        }

        // Create a unique key based on user id and selected company
        const loadKey = `${user.id}-${selectedCompanyId || 'none'}`;

        // Skip if already loaded with the same key (prevents double loads from StrictMode)
        if (lastLoadKey.current === loadKey) {
            return;
        }

        // Mark as loading BEFORE making API calls
        lastLoadKey.current = loadKey;

        // Initial load with page 1. bd-wxgk9 — honor the deep-link ?search= so a
        // pasted/refreshed /prompts?search=<id> loads pre-filtered. bd-tkpeg — an
        // isolating ?blueprintId= takes priority and filters to that one id via
        // the same id-aware backend search. Subsequent loads are handled by the
        // search/pagination effect below.
        loadPrompts(1, isolatedBlueprintId || initialSearch);
        loadCompanies();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- loadPrompts/user omitted to avoid reload loops
    }, [user?.id, user?.role, selectedCompanyId]);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reload prompts when search or page changes (but NOT on initial load - that's handled above)
    useEffect(() => {
        if (!user) {
            return;
        }

        // bd-tkpeg — when isolated to one blueprint, the id IS the effective
        // backend filter (id-aware search); the free-text term is ignored until
        // isolation is cleared.
        const effectiveSearch = isolatedBlueprintId || debouncedSearchTerm;

        // Create a key for this search/page/onlyMine combination
        const searchPageKey = `${currentPage}-${effectiveSearch}-${showOnlyMyBlueprints ? 'mine' : 'all'}`;

        // Skip if this is the same as the last search/page key (prevents duplicates)
        if (searchPageKey === lastSearchPageKey.current) {
            return;
        }

        // Update the key BEFORE making the API call
        lastSearchPageKey.current = searchPageKey;

        loadPrompts(currentPage, effectiveSearch, showOnlyMyBlueprints);
    }, [debouncedSearchTerm, isolatedBlueprintId, currentPage, showOnlyMyBlueprints, user, loadPrompts]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm, showOnlyMyBlueprints]);

    // "Show only my blueprints" is now a BACKEND filter (bd zh7jq): when the
    // box is checked the server returns strictly the authenticated user's
    // blueprints, so there is NO client-side user filter here. We only apply
    // the default updatedAt-DESC ordering.
    const displayedPrompts = useMemo(() => sortByUpdatedDesc(prompts), [prompts]);

    // bd-tkpeg — resolve the isolated blueprint's name from the loaded result
    // for the banner label (the list holds exactly one row when isolated).
    const isolatedBlueprintName = isolatedBlueprintId
        ? displayedPrompts.find((p) => p.id === isolatedBlueprintId)?.name
        : undefined;

    // bd-tkpeg — clear the isolation: drop local state and strip the URL param,
    // restoring the normal free-text search input + full list (mirrors the
    // ScheduledJobs ?jobId= clearIsolation).
    const clearIsolation = useCallback(() => {
        setIsolatedBlueprintId('');
        const next = new URLSearchParams(searchParams);
        next.delete('blueprintId');
        setSearchParams(next, {replace: true});
    }, [searchParams, setSearchParams]);

    // Pagination is fully server-side for both checked and unchecked states.
    const paginationInfo: PaginationInfo = {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        totalCount: totalCount,
        totalPages: totalPages,
        hasNextPage: currentPage < totalPages,
        hasPreviousPage: currentPage > 1,
        nextPage: currentPage < totalPages ? currentPage + 1 : null,
        previousPage: currentPage > 1 ? currentPage - 1 : null
    };

    // A full page (or more) of results should FILL the column down to the
    // pagination and scroll inside; a sparse result set should hug its rows so
    // the card never stretches a few rows into a tall empty box (bd-n3uyh).
    // Trigger strictly on the TOTAL result count, so e.g. page 9 of 1789 still
    // counts as full even though only a handful of rows are visible.
    const isFullPage = paginationInfo.totalCount >= ITEMS_PER_PAGE;

    // Reload with the CURRENT active filters (bd-sc1z) — used after
    // save/delete so the table doesn't reset to page 1 / unfiltered. Companies
    // are re-fetched too: a save with a never-used advertiser creates a company
    // server-side that the mount-time list doesn't have (deepcontext-ncjj).
    const reload = useCallback(
        async () => {
            await Promise.all([
                loadPrompts(currentPage, isolatedBlueprintId || debouncedSearchTerm, showOnlyMyBlueprints),
                loadCompanies(),
            ]);
        },
        [loadPrompts, loadCompanies, currentPage, isolatedBlueprintId, debouncedSearchTerm, showOnlyMyBlueprints],
    );

    return {
        prompts: displayedPrompts,
        companies,
        isLoading,
        searchTerm,
        setSearchTerm,
        showOnlyMyBlueprints,
        setShowOnlyMyBlueprints,
        isolatedBlueprintId,
        isolatedBlueprintName,
        clearIsolation,
        currentPage,
        setCurrentPage,
        paginationInfo,
        isFullPage,
        reload,
    };
}
